import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Check, EyeOff, RefreshCw, Flag, ImageOff, Upload } from "lucide-react";
import api, { API, formatError } from "@/lib/api";

const FILTERS = [
  { id: "flagged", label: "Flagged" },
  { id: "pending", label: "To validate" },
  { id: "redrawn", label: "Redrawn" },
  { id: "hidden", label: "Hidden" },
  { id: "approved", label: "Approved" },
  { id: "visible", label: "All visible" },
];
const LEVELS = { 1: "Easy", 2: "Medium", 3: "Difficult" };
const REASONS = { missing: "Picture missing", wrong: "Wrong picture", wrong_count: "Wrong count", wrong_numbers: "Wrong numbers", shows_answer: "Shows the answer", other: "Other" };
const BTN = "rounded-full px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-50 transition-colors";

function StatusBadge({ item }) {
  const pending = item.status === "pending";
  const cls = pending ? "bg-amber-500 text-slate-900" : item.hidden ? "bg-red-900/60 text-red-200" : "bg-green-900/60 text-green-200";
  const label = pending ? "waiting for your OK" : item.hidden ? "hidden" : ({ ok: "checked", redrawn: "redrawn", approved: "approved" }[item.status] || item.status);
  return <span className={`px-2 py-0.5 rounded-full font-semibold ${cls}`} data-testid="picture-status">{label}</span>;
}

function PictureActions({ item, onAction, onUpload, busy }) {
  const fileRef = useRef(null);
  return (
    <div className="flex flex-wrap gap-2 mt-3">
      <button disabled={!!busy} onClick={() => onAction(item, "approve")} data-testid="picture-approve" className={`${BTN} bg-green-500 text-slate-900 hover:bg-green-400`}><Check size={13} /> Approve</button>
      <button disabled={!!busy} onClick={() => onAction(item, "hide")} data-testid="picture-hide" className={`${BTN} bg-slate-700 hover:bg-slate-600`}><EyeOff size={13} /> Hide</button>
      <button disabled={!!busy} onClick={() => onAction(item, "redraw")} data-testid="picture-redraw" className={`${BTN} bg-yellow-300 text-slate-900 hover:bg-yellow-200`}>
        {busy === "redraw" ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} {busy === "redraw" ? "Redrawing…" : "Redraw"}
      </button>
      <button disabled={!!busy} onClick={() => fileRef.current?.click()} data-testid="picture-upload" className={`${BTN} bg-sky-400 text-slate-900 hover:bg-sky-300`}>
        {busy === "upload" ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} {busy === "upload" ? "Uploading…" : "Upload my picture"}
      </button>
      <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="picture-upload-input"
        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) onUpload(item, f); }} />
    </div>
  );
}

function PictureCard({ item, onAction, onUpload, busy, version }) {
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [version]);
  const chapter = (item.chapter_id || "").split("|").slice(2).join(" ");
  return (
    <div className={`bg-slate-800 rounded-2xl border-2 p-4 flex gap-4 ${item.status === "pending" ? "border-amber-500/70" : "border-slate-700"}`} data-testid={`picture-card-${item.key}`}>
      <div className="w-48 h-32 shrink-0 rounded-xl bg-white flex items-center justify-center overflow-hidden">
        {broken ? <ImageOff className="text-slate-400" /> : (
          <img src={`${API}/question-image/preview/${encodeURIComponent(item.key)}?v=${version}`} alt=""
            onError={() => setBroken(true)} className="w-full h-full object-contain" data-testid="picture-preview" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap gap-1.5 text-xs mb-1.5">
          <span className="px-2 py-0.5 rounded-full bg-slate-700">Grade {item.grade ?? "?"}</span>
          <span className="px-2 py-0.5 rounded-full bg-slate-700">{LEVELS[item.level] || "—"} · Q{(item.qi ?? 0) + 1}</span>
          <span className="px-2 py-0.5 rounded-full bg-slate-700 uppercase">{item.lang}</span>
          <StatusBadge item={item} />
          {item.uploaded && <span className="px-2 py-0.5 rounded-full bg-sky-900/60 text-sky-200" data-testid="picture-uploaded-badge">your picture</span>}
          {item.flag_count > 0 && <span className="px-2 py-0.5 rounded-full bg-amber-900/60 text-amber-200 inline-flex items-center gap-1"><Flag size={11} /> {item.flag_count}</span>}
        </div>
        <p className="font-semibold text-slate-100" data-testid="picture-question">{item.question}</p>
        <p className="text-xs text-slate-400 mt-0.5">{chapter} · answer: {item.answer}</p>
        {item.status === "pending" && <p className="text-xs text-amber-300 mt-1" data-testid="picture-pending-note">New picture — children don't see it until you click Approve.</p>}
        {item.flags?.map((f, i) => (
          <p key={i} className="text-xs text-amber-200 mt-1" data-testid={`picture-flag-${i}`}>
            {f.reason && <span className="font-semibold px-1.5 py-0.5 rounded bg-amber-900/60 mr-1.5">{REASONS[f.reason] || f.reason}</span>}
            {f.note && <>“{f.note}” </>}<span className="text-slate-400">— {f.by}</span>
          </p>
        ))}
        <PictureActions item={item} onAction={onAction} onUpload={onUpload} busy={busy} />
      </div>
    </div>
  );
}

const REDRAW_MSG = { pending: ["success", "New picture drawn — check it, then click Approve"], hidden: ["warning", "Redraw failed the check — picture stays hidden"] };

export default function PictureReview() {
  const [filter, setFilter] = useState("flagged");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState({});
  const [version, setVersion] = useState(0);

  const load = (f = filter) => api.get("/admin/pictures", { params: { filter: f } }).then(({ data }) => setData(data)).catch(() => setData({ counts: {}, items: [] }));
  useEffect(() => { setData(null); load(filter); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async (item, action, call, done) => {
    setBusy((b) => ({ ...b, [item.key]: action }));
    try {
      done(await call());
      setVersion((v) => v + 1);
      await load();
    } catch (e) {
      toast.error(formatError(e?.response?.data?.detail));
    } finally {
      setBusy((b) => ({ ...b, [item.key]: null }));
    }
  };

  const onAction = (item, action) => run(item, action, () => api.post(`/admin/pictures/${encodeURIComponent(item.key)}/${action}`), ({ data: r }) => {
    if (action !== "redraw") return toast.success(action === "approve" ? "Picture approved — children see it now" : "Picture hidden");
    const [kind, msg] = REDRAW_MSG[r.status] || ["success", "New picture drawn and checked"];
    toast[kind](msg);
  });

  const onUpload = (item, file) => run(item, "upload", () => {
    const fd = new FormData();
    fd.append("file", file);
    return api.post(`/admin/pictures/${encodeURIComponent(item.key)}/upload`, fd);
  }, () => toast.success("Your picture is saved — children see it now"));

  return (
    <div data-testid="admin-pictures">
      <div className="flex flex-wrap gap-2 mb-5">
        {FILTERS.map((f) => (
          <button key={f.id} onClick={() => setFilter(f.id)} data-testid={`picture-filter-${f.id}`}
            className={`px-3 py-1 rounded-full border-2 text-xs font-semibold ${filter === f.id ? "bg-yellow-300 text-slate-900 border-yellow-300" : "border-slate-600 text-slate-300"}`}>
            {f.label} ({data?.counts?.[f.id] ?? "…"})
          </button>
        ))}
      </div>
      {!data && <div className="flex justify-center py-10"><Loader2 className="animate-spin text-yellow-300" /></div>}
      {data && data.items.length === 0 && <p className="text-slate-400 text-center py-8" data-testid="pictures-empty">No pictures here.</p>}
      <div className="space-y-3">
        {data?.items.map((it) => <PictureCard key={it.key} item={it} busy={busy[it.key]} onAction={onAction} onUpload={onUpload} version={version} />)}
      </div>
    </div>
  );
}
